import React, { useState, useMemo } from "react";
import {
  Box,
  VStack,
  Text,
  useDisclosure,
  ButtonGroup,
  Badge,
  Spinner,
  HStack,
  Flex,
  Icon,
} from "@chakra-ui/react";
import { useUserContext } from "../../../../Contexts/userContext";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import { useStudentsContext } from "../../../../Contexts/StudentsContext";
import {
  DeleteDataRequest,
  DeletionHistoryItem,
  ApiDataResponse,
  DeletionResult,
  PaginatedDeletionHistory,
} from "../../../../services/Dtos";
import useAxios from "../../../../hooks/useAxios";
import { useStyles } from "../../../../hooks/useStyles";
import useThemeColors from "../../../../hooks/useThemeColors";
import DataCustomizationCard from "./DataCustomizationCard";
import Cad from "../../../CustomUi/Cad";
import SectionHeader from "../../../CustomUi/SectionHeader";
import useIcons from "../../../../hooks/useIcons";
import DeleteAlertDialogModal from "../../../CustomUi/DeleteAlertDialogModal";
import CustomButton from "../../../CustomUi/CustomButton";
import CustomPopover from "../../../CustomUi/CustomPopover";
import SelectInput from "../../../CustomUi/SelectInput";
import TextInput from "../../../CustomUi/TextInput";
import CustomCheckBox from "../../../CustomUi/CustomCheckBox";
import CustomModal from "../../../CustomUi/CustomModal";
import CustomAlert from "../../../CustomUi/CustomAlert";
import Pagination from "../../../CustomUi/Pagination";
import useToaster from "../../../../hooks/useToaster";
import TextAreaInput from "../../../CustomUi/TextAreaInput";
import CustomStep from "../../../CustomUi/CustomStep";
import { StepsContent } from "../../../ui/steps";

const getStatusInfo = (status: number, icons: any) => {
  switch (status) {
    case 0:
      return {
        label: "Pending",
        colorPalette: "gray",
        icon: icons.utility.clock,
      };
    case 1:
      return {
        label: "Processing",
        colorPalette: "yellow",
        icon: icons.utility.clock,
      };
    case 2:
      return {
        label: "Deleted",
        colorPalette: "green",
        icon: icons.utility.check,
      };
    case 3:
      return {
        label: "Failed",
        colorPalette: "red",
        icon: icons.utility.clock,
      };
    case 4:
      return {
        label: "Cancelled",
        colorPalette: "orange",
        icon: icons.utility.cancel,
      };
    default:
      return {
        label: "Unknown",
        colorPalette: "gray",
        icon: icons.utility.clock,
      };
  }
};

const formatUtcToLocal = (dateString: string) => {
  const correctedDateString = dateString.endsWith("Z")
    ? dateString
    : `${dateString}Z`;
  return new Date(correctedDateString).toLocaleString();
};

const truncateDeletionSummary = (summary: string) => {
  if (!summary) return summary;

  const firstOpenParen = summary.indexOf("(");
  if (firstOpenParen === -1) return summary;

  const secondOpenParen = summary.indexOf("(", firstOpenParen + 1);
  if (secondOpenParen === -1) return summary;

  return summary.substring(0, secondOpenParen).trim();
};

const DeleteData: React.FC = () => {
  const { user } = useUserContext();
  const { showSuccessToast, showErrorToast } = useToaster();
  const [isOpen, setIsOpen] = useState(false);
  const {
    open: isHistoryOpen,
    onOpen: onHistoryOpen,
    onClose: onHistoryClose,
  } = useDisclosure();
  const { classes, levels } = useSchoolsContext();
  const { students, fetchStudents } = useStudentsContext();
  const axiosClient = useAxios();
  const { scrollBar } = useStyles();
  const colors = useThemeColors();
  const icons = useIcons();

  // Stepper state
  const [currentStep, setCurrentStep] = useState(0);

  // Form state
  const [classId, setClassId] = useState("");
  const [termId] = useState("");
  const [sessionId] = useState("");
  const [levelId, setLevelId] = useState("");
  const [studentIds, setStudentIds] = useState<string[]>([]);
  const [deleteAcademicRecords, setDeleteAcademicRecords] = useState(true);
  const [deletePersonalInfo, setDeletePersonalInfo] = useState(false);
  const [deleteGuardianInfo, setDeleteGuardianInfo] = useState(false);
  const [deletionReason, setDeletionReason] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [confirmationEmail, setConfirmationEmail] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [history, setHistory] = useState<DeletionHistoryItem[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const steps = [
    { title: "Select Students" },
    { title: "Choose Data" },
    { title: "Confirm & Delete" },
  ];

  const filteredStudents = useMemo(() => {
    return students.filter((student) => {
      const studentClass = classes.find((c) => c.id === student.currentClassId);
      const matchesSearch = `${student.firstName} ${student.lastName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesClass = !classId || student.currentClassId === classId;
      const matchesLevel =
        !levelId || (studentClass && studentClass.levelId === levelId);

      return matchesSearch && matchesClass && matchesLevel;
    });
  }, [students, searchTerm, classId, levelId, classes]);

  const fetchHistory = async (page = 1) => {
    setIsHistoryLoading(true);
    try {
      const response = await axiosClient.get<
        ApiDataResponse<PaginatedDeletionHistory>
      >(`DataDeletion/history?pageNumber=${page}&pageSize=${pageSize}`);
      if (response.data.succeeded) {
        setHistory(response.data.data.data);
        setCurrentPage(response.data.data.pageNumber);
        setTotalPages(response.data.data.totalPages);
        setTotalRecords(response.data.data.totalRecords);
      } else {
        showErrorToast("Failed to fetch deletion history.");
      }
    } catch (err) {
      showErrorToast("An error occurred while fetching history.");
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const handleHistoryOpen = () => {
    onHistoryOpen();
    fetchHistory();
  };

  const handleStudentCheckboxChange = (studentId: string) => {
    setStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAllStudents = () => {
    setStudentIds(filteredStudents.map((s) => s.id));
  };

  const handleDeselectAllStudents = () => {
    setStudentIds([]);
  };

  const isStep1Complete = studentIds.length > 0;
  const isStep3Complete =
    !!deletionReason &&
    confirmationEmail.toLowerCase() === user?.email?.toLowerCase();

  if (user?.role !== 3) {
    return (
      <CustomAlert
        status="error"
        title="Access Denied"
        message="Only administrators can delete data."
      />
    );
  }

  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    const requestBody: DeleteDataRequest = {
      classId: classId || undefined,
      termId: termId || undefined,
      sessionId: sessionId || undefined,
      levelId: levelId || undefined,
      studentIds,
      deleteAcademicRecords,
      deleteBehavioralRecords: false,
      deletePersonalInfo,
      deleteGuardianInfo,
      deletionReason,
      additionalNotes,
    };

    try {
      const response = await axiosClient.post<ApiDataResponse<DeletionResult>>(
        "DataDeletion/delete",
        requestBody
      );

      if (response.data.succeeded) {
        showSuccessToast(
          "Deletion successful. You can see the history of your deletions in the history tab."
        );
        await fetchStudents();
        setIsOpen(false);
        // Reset form
        setCurrentStep(1);
        setStudentIds([]);
        setDeletionReason("");
        setAdditionalNotes("");
        setConfirmationEmail("");
      } else {
        setError(response.data.message || "Failed to start deletion.");
      }
    } catch (err) {
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeletion = async (deletionId: string) => {
    setIsCancelling((prev) => ({ ...prev, [deletionId]: true }));
    try {
      const response = await axiosClient.post(
        `DataDeletion/cancel/${deletionId}`
      );
      if (response.data.succeeded) {
        showSuccessToast(
          "Deletion cancelled. The deletion process has been stopped."
        );
        await fetchHistory(currentPage);
      } else {
        showErrorToast(response.data.message || "Failed to cancel deletion.");
      }
    } catch (err) {
      showErrorToast("Could not communicate with the server to cancel.");
    } finally {
      setIsCancelling((prev) => ({ ...prev, [deletionId]: false }));
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchHistory(page);
  };

  const getSelectedDataTypes = () => {
    const types = [];
    if (deleteAcademicRecords) types.push("Academic Records");
    if (deletePersonalInfo) types.push("Personal Info");
    if (deleteGuardianInfo) types.push("Guardian Info");
    return types;
  };

  const classOptions = classes.map((c) => ({
    label: c.className,
    value: c.id,
  }));

  const levelOptions = levels.map((l) => ({
    label: l.levelName,
    value: l.id,
  }));

  const isNextDisabled =
    (currentStep === 0 && !isStep1Complete) ||
    (currentStep === 1 && !getSelectedDataTypes().length);

  return (
    <VStack gap={[2, 4]} align={"center"}>
      <CustomStep
        steps={steps}
        currentStep={currentStep}
        onStepChange={setCurrentStep}
        isNextDisabled={isNextDisabled}
        size="xs"
      >
        <Box flex={1} minH={0} w={"full"}>
          <StepsContent index={0}>
            <Cad flex={1}>
              {/* Header */}
              <SectionHeader
                title="Select Students"
                subtitle="Use the filter button to select students by class or level."
                icon={icons.people.students}
                buttonIcon={icons.utility.history}
                buttonText="View History"
                onButtonClick={handleHistoryOpen}
              />
              <VStack gap={3} align="stretch" flexShrink={0}>
                <HStack
                  gap={3}
                  flexDir={["column", "row"]}
                  align={["stretch", "center"]}
                >
                  <TextInput
                    label=""
                    name="search"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    startElement={<Icon as={icons.utility.search} />}
                    placeholder="Search students in filtered list"
                  />
                  <CustomPopover
                    w="300px"
                    header={<Text fontWeight="semibold">Student Filters</Text>}
                    trigger={
                      <CustomButton
                        size="sm"
                        variant="surface"
                        colorPalette={colors.schoolAccent.primary}
                        label="Filters"
                        icon={icons.utility.filter}
                      />
                    }
                  >
                    <VStack gap={3}>
                      <SelectInput
                        label="Class"
                        name="classId"
                        value={classId}
                        onChange={(e) => setClassId(e.target.value)}
                        collection={classOptions}
                        placeholder="All Classes"
                      />
                      <SelectInput
                        label="Level"
                        name="levelId"
                        value={levelId}
                        onChange={(e) => setLevelId(e.target.value)}
                        collection={levelOptions}
                        placeholder="All Levels"
                      />
                    </VStack>
                  </CustomPopover>
                  <ButtonGroup size="sm">
                    <CustomButton
                      colorPalette={colors.schoolAccent.primary}
                      label="Select All"
                      onClick={handleSelectAllStudents}
                      variant="outline"
                    />
                    <CustomButton
                      colorPalette={colors.schoolAccent.primary}
                      label="Clear"
                      onClick={handleDeselectAllStudents}
                      variant="outline"
                    />
                  </ButtonGroup>
                </HStack>
                <Text fontSize="sm" color={colors.text.secondary}>
                  Showing {filteredStudents.length} of {students.length}{" "}
                  students
                </Text>
              </VStack>
              <Box
                overflowY="auto"
                borderTop="1px solid"
                borderColor={colors.border.primary}
                css={scrollBar}
              >
                {/* Students List */}
                <VStack align="stretch" gap={1}>
                  {filteredStudents.map((student) => (
                    <Flex
                      key={student.id}
                      align="center"
                      p={2}
                      borderRadius="md"
                      cursor="pointer"
                      onClick={() => handleStudentCheckboxChange(student.id)}
                      bg={
                        studentIds.includes(student.id)
                          ? colors.background.tertiary
                          : "transparent"
                      }
                      _hover={{
                        bg: colors.background.tertiary,
                      }}
                    >
                      <Box onClick={(e) => e.stopPropagation()}>
                        <CustomCheckBox
                          checked={studentIds.includes(student.id)}
                          onChange={() =>
                            handleStudentCheckboxChange(student.id)
                          }
                        />
                      </Box>
                      <HStack justify="space-between" w="full" ml={4}>
                        <Text fontWeight="medium" fontSize="sm">
                          {student.firstName} {student.lastName}
                        </Text>
                        <Badge
                          variant="subtle"
                          colorPalette="gray"
                          fontSize="0.7em"
                        >
                          {classes.find((c) => c.id === student.currentClassId)
                            ?.className || "No class"}
                        </Badge>
                      </HStack>
                    </Flex>
                  ))}
                </VStack>
              </Box>
            </Cad>
          </StepsContent>
          <StepsContent index={1}>
            <Cad overflow={"auto"}>
              <SectionHeader
                title="Choose Data"
                subtitle="Select the types of data to delete for the selected students."
                icon={icons.main.subject}
              />
              <VStack gap={6} align="stretch">
                <DataCustomizationCard
                  isChecked={deleteAcademicRecords}
                  onChange={setDeleteAcademicRecords}
                  icon={icons.main.academic}
                  title="Academic Records"
                  description="Grades, test scores, etc."
                />
                <DataCustomizationCard
                  isChecked={deletePersonalInfo}
                  onChange={setDeletePersonalInfo}
                  icon={icons.people.student}
                  title="Personal Info"
                  description="Student's personal details."
                />
                <DataCustomizationCard
                  isChecked={deleteGuardianInfo}
                  onChange={setDeleteGuardianInfo}
                  icon={icons.people.guardian}
                  title="Guardian Info"
                  description="Parent/guardian details."
                />
              </VStack>
            </Cad>
          </StepsContent>
          <StepsContent index={2}>
            <Cad overflow={"auto"}>
              <SectionHeader
                title="Confirm Deletion"
                subtitle="Confirm the deletion of the selected data."
                icon={icons.utility.delete}
              />
              <VStack gap={6} align="stretch">
                <HStack
                  gap={4}
                  align="stretch"
                  flexDirection={["column", "column", "row"]}
                >
                  <Box flex={1}>
                    <TextAreaInput
                      value={deletionReason}
                      onChange={(e) => setDeletionReason(e.target.value)}
                      label="Reason for Deletion"
                      name="deletionReason"
                      placeholder="e.g., End of session cleanup, data correction, student transfer..."
                      required
                    />
                  </Box>

                  <Box flex={1}>
                    <TextAreaInput
                      value={additionalNotes}
                      onChange={(e) => setAdditionalNotes(e.target.value)}
                      placeholder="Any additional context or comments..."
                      label="Additional Notes (Optional)"
                      name="additionalNotes"
                    />
                  </Box>
                </HStack>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Confirm Your Email
                  </Text>
                  <TextInput
                    label="Type your email to confirm"
                    name="confirmationEmail"
                    type="email"
                    value={confirmationEmail}
                    onChange={(e) => setConfirmationEmail(e.target.value)}
                    invalid={!isStep3Complete && confirmationEmail.length > 0}
                    error={
                      !isStep3Complete && confirmationEmail.length > 0
                        ? "Email must match your account email"
                        : ""
                    }
                  />
                  <Text fontSize="xs" color={colors.text.secondary} mt={1}>
                    For security, please enter your email address to confirm
                    this action.
                  </Text>
                </Box>
                <Box
                  p={4}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={colors.status.errorBorder}
                  bg={colors.status.errorBg}
                >
                  <VStack align="start" gap={2}>
                    <HStack>
                      <Icon as={icons.utility.error} color="#E53E3E" />
                      <Text fontWeight="semibold" color="red.600">
                        Deletion Summary
                      </Text>
                    </HStack>
                    <Text fontSize="sm">
                      <strong>Students:</strong> {studentIds.length} selected
                    </Text>
                    <Text fontSize="sm">
                      <strong>Data Types:</strong>{" "}
                      {getSelectedDataTypes().join(", ") || "None selected"}
                    </Text>
                    <Text fontSize="sm">
                      <strong>Reason:</strong>{" "}
                      {deletionReason || "No reason provided"}
                    </Text>
                  </VStack>
                </Box>
              </VStack>
              <HStack
                flexDirection={["column", "row"]}
                align={["stretch", "center"]}
                justify={"flex-end"}
                w="full"
              >
                <CustomButton
                  my={3}
                  colorPalette="red"
                  label="Request Deletion"
                  icon={icons.utility.delete}
                  onClick={() => setIsOpen(true)}
                  loading={loading}
                  disabled={!isStep3Complete}
                />
              </HStack>
            </Cad>
          </StepsContent>
        </Box>
      </CustomStep>

      {error && <CustomAlert status="error" title="Error" message={error} />}

      {/* History Modal */}
      <CustomModal
        isOpen={isHistoryOpen}
        onClose={onHistoryClose}
        handleClose={onHistoryClose}
        title="Deletion History"
        icon={icons.utility.history}
        hideCancelButton={true}
        actionText="Close"
        handleAction={onHistoryClose}
        hasChanges={true}
      >
        <Box>
          {isHistoryLoading ? (
            <Flex justify="center" align="center" h="100px">
              <Spinner size="lg" color={colors.schoolAccent.primary} />
            </Flex>
          ) : (
            <VStack gap={3} align="stretch">
              {history.length > 0 ? (
                history.map((item) => {
                  const statusInfo = getStatusInfo(item.status, icons);
                  const StatusIcon = statusInfo.icon;

                  return (
                    <Box
                      key={item.deletionId}
                      p={4}
                      borderWidth="1px"
                      borderRadius="lg"
                      borderColor={colors.border.primary}
                      bg={colors.background.secondary}
                      w="full"
                      transition="all 0.2s"
                      _hover={{
                        bg: colors.schoolAccent.hover,
                        transform: "translateY(-1px)",
                      }}
                    >
                      <Flex justify="space-between" align="start" mb={3}>
                        <VStack align="start" gap={2}>
                          <HStack>
                            <Icon as={StatusIcon} boxSize={4} />
                            <Badge
                              colorPalette={statusInfo.colorPalette}
                              variant="subtle"
                              fontSize="xs"
                              fontWeight="medium"
                            >
                              {statusInfo.label}
                            </Badge>
                          </HStack>
                          <Text fontSize="xs" color={colors.text.secondary}>
                            {formatUtcToLocal(item.deletionDate)}
                          </Text>
                        </VStack>
                        {(item.status === 0 || item.status === 1) && (
                          <CustomButton
                            size="xs"
                            colorPalette="red"
                            variant="outline"
                            label="Cancel"
                            onClick={() =>
                              handleCancelDeletion(item.deletionId)
                            }
                            loading={isCancelling[item.deletionId]}
                          />
                        )}
                      </Flex>

                      <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        mb={3}
                        color={colors.text.primary}
                      >
                        {truncateDeletionSummary(item.deletionSummary)}
                      </Text>

                      {item.deletionReason && (
                        <Box
                          p={3}
                          bg={colors.background.secondary}
                          borderRadius="md"
                          borderLeft="3px solid"
                          borderLeftColor={colors.schoolAccent.primary}
                        >
                          <Text
                            fontSize="xs"
                            color={colors.text.secondary}
                            fontWeight="medium"
                          >
                            <strong>Reason:</strong> {item.deletionReason}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  );
                })
              ) : (
                <Box textAlign="center" py={8} color={colors.text.secondary}>
                  <Icon
                    as={icons.utility.history}
                    boxSize={8}
                    mb={2}
                    opacity={0.5}
                  />
                  <Text fontSize="sm">No deletion history found.</Text>
                  <Text fontSize="xs" mt={1}>
                    Deletion requests will appear here once submitted.
                  </Text>
                </Box>
              )}
            </VStack>
          )}
        </Box>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={totalRecords}
          currentPageRecords={history.length}
          onPreviousPage={() => handlePageChange(currentPage - 1)}
          onNextPage={() => handlePageChange(currentPage + 1)}
          loading={loading}
        />
      </CustomModal>

      {/* Confirmation Modal */}
      <DeleteAlertDialogModal
        openDeleteDialog={isOpen}
        setOpenDeleteDialog={setIsOpen}
        handleDelete={handleDelete}
        isDeleting={loading}
        heading="Confirm Deletion"
        customMessage={
          <VStack align="start" gap={3}>
            <CustomAlert
              status="error"
              title="This action cannot be undone!"
              message={`You are about to permanently delete data for ${studentIds.length} student(s).`}
            />

            <Box>
              <Text fontWeight="semibold" mb={1}>
                Data to be deleted:
              </Text>
              <Text fontSize="sm" color={colors.text.secondary}>
                {getSelectedDataTypes().join(", ") || "No data types selected"}
              </Text>
            </Box>

            <Box>
              <Text fontWeight="semibold" mb={1}>
                Reason:
              </Text>
              <Text fontSize="sm" color={colors.text.secondary}>
                {deletionReason || "No reason provided"}
              </Text>
            </Box>
          </VStack>
        }
      />
    </VStack>
  );
};

export default DeleteData;
